/** Every workspace (a Ryuboard board), and who is in the selected one. */

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button, focusRing } from "@/components/button";
import { PageHeader, panelClass } from "@/components/display";
import { Select } from "@/components/fields";
import { Empty, LoadError, Loading } from "@/components/states";
import { useUiStore } from "@/stores/ui-store";
import { useWorkspaces } from "./hooks";
import { MembersPanel } from "./members-panel";
import { NewWorkspaceDialog } from "./new-workspace-dialog";

export function WorkspacesPage() {
  const { data: workspaces, isPending, isError, error } = useWorkspaces();
  const selected = useUiStore((s) => s.selectedWorkspace);
  const select = useUiStore((s) => s.selectWorkspace);
  const [creating, setCreating] = useState(false);
  // Derived, not stored: the selection is an id; fall back to the first workspace.
  const open = workspaces?.find((w) => w.wsid === selected) ?? workspaces?.[0] ?? null;

  const newButton = (
    <Button tone="primary" onClick={() => setCreating(true)}>
      <Plus size={16} /> New workspace
    </Button>
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Workspaces" description="A Ryuboard board is a workspace. Roles here apply in every product."
        action={newButton} />
      {isPending ? (
        <div className={panelClass}><Loading what="workspaces" /></div>
      ) : isError ? (
        <LoadError what="workspaces" error={error} />
      ) : workspaces.length === 0 || open === null ? (
        <div className={panelClass}>
          <Empty title="No workspaces yet" action={newButton}>
            Create one for each team or board, then add the people who work in it.
          </Empty>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-[15rem_1fr] md:gap-6">
          <Select className="md:hidden" aria-label="Workspace" value={open.wsid} onChange={(e) => select(e.target.value)}>
            {workspaces.map((w) => <option key={w.wsid} value={w.wsid}>{w.name}</option>)}
          </Select>
          <nav aria-label="Workspaces" className="hidden h-fit space-y-0.5 md:block">
            {workspaces.map((w) => (
              <button key={w.wsid} onClick={() => select(w.wsid)} aria-current={w.wsid === open.wsid}
                className={`block w-full truncate rounded-lg px-3 py-2 text-left text-sm transition-colors ${focusRing} ${
                  w.wsid === open.wsid
                    ? "bg-white font-medium text-zinc-950 shadow-xs ring-1 ring-zinc-200 dark:bg-zinc-900 dark:text-white dark:ring-zinc-800"
                    : "text-zinc-600 hover:bg-zinc-200/60 dark:text-zinc-400 dark:hover:bg-zinc-800/60"
                }`}>
                {w.name}
              </button>
            ))}
          </nav>
          <MembersPanel key={open.wsid} workspace={open} />
        </div>
      )}
      <NewWorkspaceDialog open={creating} onClose={() => setCreating(false)} onCreated={select} />
    </div>
  );
}
